// Shared command helpers for the newsletter editor.
//
// All structural mutations go through these. They follow the architecture
// rule: no direct DOM mutation, all changes via ProseMirror transactions,
// selection explicitly re-set so subsequent commands operate on a sane cursor.

import { NodeSelection, TextSelection } from "@tiptap/pm/state"

// Run a shared command function (one of the ones in this module) outside of
// a chain — typically from a NodeView event handler. The command receives the
// same `{state, dispatch, tr, view, editor}` shape Tiptap chains pass, so the
// shared invariants (doc-never-empty, selection placement, etc.) hold no
// matter how the command is invoked.
export function runStructuralCommand(editor, command) {
  const view = editor.view
  const props = {
    state: view.state,
    dispatch: view.dispatch.bind(view),
    tr: view.state.tr,
    view,
    editor,
  }
  return command(props)
}

// Locate a top-level block by absolute position.
function topLevelChildAt(doc, pos) {
  let found = null
  doc.forEach((node, offset, index) => {
    if (found) return
    if (offset === pos) found = { node, offset, index }
  })
  return found
}

// Insert a top-level newsletterBlock after the current top-level block.
// Returns a Tiptap command function.
//
// Selection after:
//   - For atom nodes (divider, future image): NodeSelection on the inserted node.
//   - For container nodes (paragraph, heading): TextSelection near the start.
export function insertBlockAfter(typeName, attrs = {}) {
  return ({ state, dispatch, tr, view }) => {
    const nodeType = state.schema.nodes[typeName]
    if (!nodeType) return false

    const node = nodeType.createAndFill(attrs)
    if (!node) return false

    const $from = state.selection.$from
    // When the cursor is at the doc root (no parent), insert at current pos.
    // Otherwise insert immediately after the top-level block containing $from.
    const insertPos = $from.depth === 0 ? $from.pos : $from.after(1)

    if (!dispatch) return true

    tr.insert(insertPos, node)

    if (nodeType.isAtom) {
      tr.setSelection(NodeSelection.create(tr.doc, insertPos))
    } else {
      const $inside = tr.doc.resolve(insertPos + 1)
      tr.setSelection(TextSelection.near($inside))
    }

    dispatch(tr.scrollIntoView())
    view?.focus()
    return true
  }
}

// Move the top-level block at `pos` one slot up.
export function moveBlockUp(pos) {
  return ({ state, dispatch, tr }) => {
    const target = topLevelChildAt(state.doc, pos)
    if (!target || target.index === 0) return false
    if (!dispatch) return true

    const node = target.node
    const prevSibling = state.doc.child(target.index - 1)
    const prevPos = pos - prevSibling.nodeSize

    tr.delete(pos, pos + node.nodeSize)
    // After the deletion, the prev sibling's start position is unchanged.
    tr.insert(prevPos, node)
    tr.setSelection(NodeSelection.create(tr.doc, prevPos))
    dispatch(tr.scrollIntoView())
    return true
  }
}

// Move the top-level block at `pos` one slot down.
export function moveBlockDown(pos) {
  return ({ state, dispatch, tr }) => {
    const target = topLevelChildAt(state.doc, pos)
    if (!target) return false
    if (target.index >= state.doc.childCount - 1) return false
    if (!dispatch) return true

    const node = target.node
    const nextSibling = state.doc.child(target.index + 1)
    // Position after the next sibling, before deletion.
    const afterNext = pos + node.nodeSize + nextSibling.nodeSize

    tr.delete(pos, pos + node.nodeSize)
    // After deletion, `afterNext` shifts left by node.nodeSize via the mapping.
    const insertAt = tr.mapping.map(afterNext, -1)
    tr.insert(insertAt, node)
    // The inserted node now starts at `insertAt`.
    tr.setSelection(NodeSelection.create(tr.doc, insertAt))
    dispatch(tr.scrollIntoView())
    return true
  }
}

// Delete the top-level block at `pos`. Ensures doc never goes empty.
export function deleteBlockAt(pos) {
  return ({ state, dispatch, tr }) => {
    const node = state.doc.nodeAt(pos)
    if (!node) return false
    if (!dispatch) return true

    tr.delete(pos, pos + node.nodeSize)
    if (tr.doc.childCount === 0) {
      const paragraphType = state.schema.nodes.newsletterParagraph
      const replacement = paragraphType?.createAndFill()
      if (replacement) tr.insert(0, replacement)
    }
    // Place caret at a safe spot near the deletion.
    const $resolved = tr.doc.resolve(Math.min(pos, tr.doc.content.size))
    tr.setSelection(TextSelection.near($resolved))
    dispatch(tr.scrollIntoView())
    return true
  }
}

// ---------------------------------------------------------------------------
// Drag/drop helpers
// ---------------------------------------------------------------------------

// Resolve a top-level block under the given viewport coordinates.
// Returns `{ node, pos, rect, index, dom }` or null. Only top-level
// newsletterBlock children of the doc are considered targets.
export function findTopLevelBlockAtCoords(view, coords) {
  const result = view.posAtCoords(coords)
  if (!result) return null

  const doc = view.state.doc
  const $pos = doc.resolve(result.pos)
  let pos = null
  let node = null
  let index = null

  if ($pos.depth >= 1) {
    pos = $pos.before(1)
    node = doc.nodeAt(pos)
    index = $pos.index(0)
  } else if ($pos.nodeAfter) {
    pos = $pos.pos
    node = $pos.nodeAfter
    index = $pos.index(0)
  } else if ($pos.nodeBefore) {
    node = $pos.nodeBefore
    pos = $pos.pos - node.nodeSize
    index = $pos.index(0) - 1
  }

  if (!node) return null
  const dom = view.nodeDOM(pos)
  if (!(dom instanceof HTMLElement)) return null
  const rect = dom.getBoundingClientRect()
  return { node, pos, rect, index, dom }
}

// Find a top-level block by its stable `id` attr. Returns `{ node, pos }`
// or null. Used to re-resolve drag sources when intermediate transactions
// may have shifted positions.
export function findTopLevelBlockById(doc, id) {
  if (!id) return null
  let found = null
  doc.forEach((node, offset) => {
    if (found) return
    if (node.attrs && node.attrs.id === id) found = { node, pos: offset }
  })
  return found
}

// Detect whether moving the source block to (targetPos, placement) would be
// a no-op — i.e. the document order wouldn't change.
//
// Two no-op cases:
//   1. Source IS the target.
//   2. The intended insert position equals either the source's start or end —
//      both of those leave the source's index unchanged.
export function isNoOpBlockMove(fromPos, targetPos, placement, doc) {
  const sourceNode = doc.nodeAt(fromPos)
  const targetNode = doc.nodeAt(targetPos)
  if (!sourceNode || !targetNode) return true
  if (fromPos === targetPos) return true

  const insertAt = placement === "after"
    ? targetPos + targetNode.nodeSize
    : targetPos
  const sourceEnd = fromPos + sourceNode.nodeSize
  if (insertAt === fromPos || insertAt === sourceEnd) return true
  return false
}

// Move the top-level block at `fromPos` to `targetPos` with `placement`
// (`"before"` | `"after"`). Single transaction: delete source, remap the
// target through the mapping, insert source at the mapped position.
//
// Selection lands on the moved node so subsequent commands operate on it.
export function moveBlockToTarget(fromPos, targetPos, placement) {
  return ({ state, dispatch, tr }) => {
    const sourceNode = state.doc.nodeAt(fromPos)
    const targetNode = state.doc.nodeAt(targetPos)
    if (!sourceNode || !targetNode) return false
    if (isNoOpBlockMove(fromPos, targetPos, placement, state.doc)) return false

    const insertAt = placement === "after"
      ? targetPos + targetNode.nodeSize
      : targetPos

    if (!dispatch) return true

    tr.delete(fromPos, fromPos + sourceNode.nodeSize)
    const mappedInsertAt = tr.mapping.map(insertAt, -1)
    tr.insert(mappedInsertAt, sourceNode)
    tr.setSelection(NodeSelection.create(tr.doc, mappedInsertAt))
    dispatch(tr.scrollIntoView())
    return true
  }
}
